import React, { useState, useEffect } from 'react';
import { PrimaryButton } from '../Buttons/Buttons';
import './ProgressiveLoader.css';

interface ProgressiveLoaderProps {
	/** Callback function to call when user clicks "Try Again" */
	onRetry?: () => void;
	/** Optional test ID for testing */
	testId?: string;
}

enum LoadingState {
	HIDDEN = 'hidden',
	LOADING = 'loading',
	LONG_LOADING = 'long-loading',
	ERROR = 'error',
}

export const ProgressiveLoader: React.FC<ProgressiveLoaderProps> = ({
	onRetry,
	testId = 'progressive-loader',
}) => {
	const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.HIDDEN);
	const [restartKey, setRestartKey] = useState(0);

	useEffect(() => {
		// Reset to hidden state on restart
		setLoadingState(LoadingState.HIDDEN);

		// 0-1 second: Show nothing
		const timer1 = setTimeout(() => {
			setLoadingState(LoadingState.LOADING);
		}, 1000);

		// 1-10 seconds: Show "Loading settings..."
		const timer2 = setTimeout(() => {
			setLoadingState(LoadingState.LONG_LOADING);
		}, 10000);

		// 10-20 seconds: Show "This is taking more time than usual..."
		const timer3 = setTimeout(() => {
			setLoadingState(LoadingState.ERROR);
		}, 20000);

		// Cleanup timers on unmount or restart
		return () => {
			clearTimeout(timer1);
			clearTimeout(timer2);
			clearTimeout(timer3);
		};
	}, [restartKey]);

	const handleRetry = () => {
		// Call the onRetry callback if provided
		if (onRetry) {
			onRetry();
		}

		// Increment restart key to trigger useEffect and restart the timing sequence
		setRestartKey((prev) => prev + 1);
	};

	// Don't render anything for the first second
	if (loadingState === LoadingState.HIDDEN) {
		return null;
	}

	// Error state (after 20 seconds)
	if (loadingState === LoadingState.ERROR) {
		return (
			<div className="progressive-loader-container" data-testid={testId}>
				<div className="progressive-loader-error">
					<svg
						className="progressive-loader-error-illustration"
						width="240"
						height="240"
						viewBox="0 0 240 240"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						{/* Person illustration */}
						<g className="person-illustration">
							{/* Head */}
							<circle cx="85" cy="45" r="18" fill="#000" />
							{/* Body - dress */}
							<path
								d="M70 70 Q85 65 100 70 L105 120 Q85 125 65 120 Z"
								fill="#B4A5D8"
								stroke="#000"
								strokeWidth="1.5"
							/>
							{/* Arms */}
							<path
								d="M65 75 L50 90"
								stroke="#000"
								strokeWidth="2"
								strokeLinecap="round"
							/>
							<path
								d="M105 75 L120 65"
								stroke="#000"
								strokeWidth="2"
								strokeLinecap="round"
							/>
							{/* Legs */}
							<path
								d="M75 120 L70 155"
								stroke="#000"
								strokeWidth="2"
								strokeLinecap="round"
							/>
							<path
								d="M95 120 L100 155"
								stroke="#000"
								strokeWidth="2"
								strokeLinecap="round"
							/>
							{/* Shoes */}
							<ellipse cx="70" cy="157" rx="6" ry="3" fill="#000" />
							<ellipse cx="100" cy="157" rx="6" ry="3" fill="#000" />
						</g>

						{/* Warning icon */}
						<g className="warning-icon" transform="translate(140, 90)">
							{/* Triangle background */}
							<path
								d="M30 10 L55 50 L5 50 Z"
								fill="#FFF4CC"
								stroke="#E6C84D"
								strokeWidth="2"
							/>
							{/* Exclamation mark */}
							<line
								x1="30"
								y1="22"
								x2="30"
								y2="38"
								stroke="#000"
								strokeWidth="3"
								strokeLinecap="round"
							/>
							<circle cx="30" cy="44" r="2" fill="#000" />
						</g>
					</svg>
					<p className="progressive-loader-error-message">
						Failed to load settings. Please try again.
					</p>
					<PrimaryButton
						onClick={handleRetry}
						className="progressive-loader-retry-button"
					>
						Try again
					</PrimaryButton>
				</div>
			</div>
		);
	}

	// Loading states (1-10 seconds and 10-20 seconds)
	return (
		<div className="progressive-loader-container" data-testid={testId}>
			<div className="progressive-loader-spinner" aria-label="Loading"></div>
			<p className="progressive-loader-text">
				{loadingState === LoadingState.LOADING
					? 'Loading settings...'
					: 'This is taking more time than usual...'}
			</p>
		</div>
	);
};
